import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CreditCard, ShieldCheck, AlertTriangle, ExternalLink,
  RefreshCw, CheckCircle2, Clock, XCircle, Loader2,
} from "lucide-react";

const PUBLISHABLE = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

const STATE_META = {
  not_connected: {
    label: "Not connected",
    tone: "bg-zinc-100 text-zinc-700 border-zinc-200",
    icon: XCircle,
    description: "Connect a Stripe account to accept online payments on your invoices.",
  },
  incomplete: {
    label: "Setup incomplete",
    tone: "bg-amber-100 text-amber-800 border-amber-200",
    icon: Clock,
    description: "Finish Stripe onboarding to unlock invoice payments.",
  },
  action_required: {
    label: "Action required",
    tone: "bg-orange-100 text-orange-800 border-orange-200",
    icon: AlertTriangle,
    description: "Stripe needs additional information before you can accept payments.",
  },
  restricted: {
    label: "Restricted",
    tone: "bg-rose-100 text-rose-800 border-rose-200",
    icon: AlertTriangle,
    description: "Your Stripe account is restricted. Resolve the issue in the Stripe dashboard.",
  },
  connected: {
    label: "Connected",
    tone: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: CheckCircle2,
    description: "You're all set to accept online payments through this workspace.",
  },
};

function formatSyncedAt(iso) {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
}

function humanRequirement(req) {
  // Stripe requirements arrive as dotted keys (e.g. "individual.dob.day").
  return String(req).replace(/_/g, " ").replace(/\./g, " › ");
}

export default function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [embedded, setEmbedded] = useState(false);
  const [connectInstance, setConnectInstance] = useState(null);
  const containerRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/stripe/connect/status");
      setStatus(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to load Stripe status";
      toast.error(msg);
      setStatus({ onboarding_status: "not_connected" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Auto-refresh on ?payments=return or ?payments=refresh from Stripe hosted onboarding.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const p = params.get("payments");
    if (p === "return" || p === "refresh") {
      loadStatus();
      // Clean the URL so browser back doesn't re-trigger.
      params.delete("payments");
      const clean = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
      window.history.replaceState({}, "", clean);
      if (p === "return") toast.success("Welcome back — checking your Stripe status…");
    }
  }, [loadStatus]);

  const state = status?.onboarding_status || "not_connected";
  const meta = STATE_META[state] || STATE_META.not_connected;
  const StateIcon = meta.icon;

  const createAccount = async () => {
    setBusy(true);
    try {
      await api.post("/stripe/connect/account");
      await loadStatus();
      await openEmbeddedOnboarding();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create Stripe account";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const openEmbeddedOnboarding = async () => {
    if (!PUBLISHABLE) {
      toast.error("REACT_APP_STRIPE_PUBLISHABLE_KEY is not configured");
      return;
    }
    setBusy(true);
    try {
      const { loadConnectAndInitialize } = await import("@stripe/connect-js");
      const instance = await loadConnectAndInitialize({
        publishableKey: PUBLISHABLE,
        fetchClientSecret: async () => {
          const { data } = await api.post("/stripe/connect/account-session");
          return data.client_secret;
        },
        appearance: {
          variables: {
            colorPrimary: "#FF5A00",
            colorBackground: "#ffffff",
            colorText: "#18181b",
            borderRadius: "10px",
          },
        },
      });
      setConnectInstance(instance);
      setEmbedded(true);
    } catch (e) {
      // Fall back to hosted onboarding.
      const msg = e?.message || "Embedded onboarding unavailable — falling back to hosted flow";
      toast.info(msg);
      await openHostedOnboarding();
    } finally {
      setBusy(false);
    }
  };

  // Mount the embedded onboarding component whenever the instance + container are ready.
  useEffect(() => {
    if (!embedded || !connectInstance || !containerRef.current) return;
    // Ensure container is empty (in case of re-mount).
    containerRef.current.innerHTML = "";
    const el = connectInstance.create("account-onboarding");
    el.setOnExit(() => {
      setEmbedded(false);
      loadStatus();
      toast.success("Stripe onboarding finished — refreshing status…");
    });
    containerRef.current.appendChild(el);
    return () => {
      try { el.remove?.(); } catch { /* ignore */ }
    };
  }, [embedded, connectInstance, loadStatus]);

  const openHostedOnboarding = async () => {
    try {
      const { data } = await api.post("/stripe/connect/onboarding-link");
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create onboarding link");
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stripe/connect/dashboard-link");
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create dashboard link");
    } finally {
      setBusy(false);
    }
  };

  const refreshStatus = async () => {
    setBusy(true);
    try {
      await loadStatus();
      toast.success("Stripe status refreshed");
    } finally {
      setBusy(false);
    }
  };

  const primaryCta = useMemo(() => {
    if (embedded) return null;
    if (state === "not_connected") {
      return (
        <Button
          onClick={createAccount}
          disabled={busy}
          className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift"
          data-testid="stripe-connect-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <CreditCard className="w-4 h-4 mr-2"/>}
          Connect Stripe
        </Button>
      );
    }
    if (state === "incomplete" || state === "action_required") {
      return (
        <Button
          onClick={openEmbeddedOnboarding}
          disabled={busy}
          className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift"
          data-testid="stripe-continue-setup-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <ShieldCheck className="w-4 h-4 mr-2"/>}
          {state === "action_required" ? "Complete verification" : "Continue setup"}
        </Button>
      );
    }
    if (state === "restricted") {
      // Slice C — Restricted-account recovery. Two paths:
      //   1) Deep-link straight into the Stripe dashboard (default; works on
      //      mobile because it opens Stripe's own responsive site).
      //   2) Fallback to *embedded* account_management so the owner can fix
      //      requirements in-app when possible (some errors are dashboard-only).
      return (
        <Button
          onClick={openDashboard}
          disabled={busy}
          className="bg-rose-600 hover:bg-rose-700 text-white btn-lift"
          data-testid="stripe-restricted-dashboard-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <ExternalLink className="w-4 h-4 mr-2"/>}
          Resolve in Stripe dashboard
        </Button>
      );
    }
    // connected
    return (
      <Button
        onClick={openDashboard}
        disabled={busy}
        variant="outline"
        className="btn-lift"
        data-testid="stripe-manage-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <ExternalLink className="w-4 h-4 mr-2"/>}
        Manage Stripe
      </Button>
    );
  }, [state, busy, embedded]);

  return (
    <div
      className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden mb-8"
      data-testid="stripe-connect-panel"
    >
      <div className="p-4 sm:p-6 border-b border-zinc-100 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-50 border border-orange-100 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-5 h-5 text-[#FF5A00]"/>
            </div>
            <div className="min-w-0">
              <h2 className="font-heading text-xl font-bold">Payments</h2>
              <p className="text-sm text-zinc-500 mt-0.5">Stripe Connect — accept card payments on your invoices.</p>
            </div>
          </div>
        </div>
        <span
          className={`text-xs px-3 py-1.5 rounded-full border font-mono-accent uppercase tracking-widest inline-flex items-center gap-1.5 self-start ${meta.tone}`}
          data-testid="stripe-status-badge"
        >
          <StateIcon className="w-3.5 h-3.5"/> {meta.label}
        </span>
      </div>

      {loading ? (
        <div className="p-6 flex items-center gap-2 text-zinc-500" data-testid="stripe-status-loading">
          <Loader2 className="w-4 h-4 animate-spin"/> Loading Stripe status…
        </div>
      ) : (
        <div className="p-4 sm:p-6 space-y-6">
          <p className="text-sm text-zinc-600" data-testid="stripe-status-description">{meta.description}</p>

          {/* Status grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatusPill
              label="Charges enabled"
              on={!!status?.charges_enabled}
              testid="stripe-charges-enabled"
            />
            <StatusPill
              label="Payouts enabled"
              on={!!status?.payouts_enabled}
              testid="stripe-payouts-enabled"
            />
            <StatusPill
              label="Details submitted"
              on={!!status?.details_submitted}
              testid="stripe-details-submitted"
            />
            <div className="rounded-lg border border-zinc-200 p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">Last synced</div>
              <div className="text-sm font-medium mt-1" data-testid="stripe-last-synced">
                {formatSyncedAt(status?.stripe_last_synced_at)}
              </div>
            </div>
          </div>

          {/* Disabled reason */}
          {status?.disabled_reason && (
            <div
              className="rounded-lg border border-rose-200 bg-rose-50 p-4"
              data-testid="stripe-disabled-reason"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-600"/>
                <div>
                  <div className="text-sm font-semibold text-rose-800">Account disabled</div>
                  <div className="text-xs text-rose-700 mt-1">
                    Reason: <span className="font-mono-accent">{status.disabled_reason}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Outstanding requirements */}
          {(status?.requirements_currently_due?.length > 0 || status?.requirements_eventually_due?.length > 0) && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="stripe-requirements">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-amber-700"/>
                <div className="text-sm font-semibold text-amber-900">Outstanding requirements</div>
              </div>
              {status.requirements_currently_due?.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-amber-800 font-mono-accent">Currently due</div>
                  <ul className="mt-1 space-y-0.5 text-xs text-amber-900">
                    {status.requirements_currently_due.map((r) => (
                      <li key={r} data-testid={`stripe-req-currently-${r}`}>• {humanRequirement(r)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {status.requirements_eventually_due?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-800 font-mono-accent">Eventually due</div>
                  <ul className="mt-1 space-y-0.5 text-xs text-amber-900">
                    {status.requirements_eventually_due.map((r) => (
                      <li key={r} data-testid={`stripe-req-eventually-${r}`}>• {humanRequirement(r)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Embedded onboarding container */}
          {embedded && (
            <div
              className="rounded-lg border border-zinc-200 p-4 bg-zinc-50"
              data-testid="stripe-embedded-onboarding"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium">Complete your Stripe onboarding below</div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setEmbedded(false); loadStatus(); }}
                  data-testid="stripe-embedded-close"
                >
                  Hide
                </Button>
              </div>
              <div ref={containerRef} data-testid="stripe-embedded-mount" className="min-h-[480px] bg-white rounded-md"/>
            </div>
          )}

          {/* Actions — Slice C: wrap on mobile, keep touch-target ≥44px */}
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2">
            {primaryCta}
            {state !== "not_connected" && (
              <Button
                onClick={refreshStatus}
                disabled={busy}
                variant="outline"
                className="btn-lift w-full sm:w-auto"
                data-testid="stripe-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`}/> Refresh status
              </Button>
            )}
            {(state === "incomplete" || state === "action_required") && !embedded && (
              <Button
                onClick={openHostedOnboarding}
                disabled={busy}
                variant="outline"
                className="btn-lift w-full sm:w-auto"
                data-testid="stripe-hosted-fallback-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2"/> Open hosted onboarding
              </Button>
            )}
            {/* Slice C — Always-available hosted management for `connected`
                and `restricted` accounts. Useful when embedded management is
                unavailable (mobile / iframe blocked / feature not supported).*/}
            {(state === "connected" || state === "restricted") && (
              <Button
                onClick={openDashboard}
                disabled={busy}
                variant="ghost"
                className="btn-lift w-full sm:w-auto text-zinc-600"
                data-testid="stripe-hosted-management-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2"/> Open Stripe dashboard
              </Button>
            )}
          </div>

          <p className="text-[11px] text-zinc-500">
            Payments are routed directly to your Stripe account. LeadForge does not hold or move your funds.
          </p>
        </div>
      )}
    </div>
  );
}

function StatusPill({ label, on, testid }) {
  return (
    <div
      className={`rounded-lg border p-3 ${on ? "border-emerald-200 bg-emerald-50" : "border-zinc-200 bg-zinc-50"}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">{label}</div>
      <div className={`mt-1 text-sm font-semibold inline-flex items-center gap-1.5 ${on ? "text-emerald-700" : "text-zinc-500"}`}>
        {on ? <CheckCircle2 className="w-4 h-4"/> : <XCircle className="w-4 h-4"/>}
        {on ? "Yes" : "No"}
      </div>
    </div>
  );
}
